// src/components/lists/BookingsList/bookingsListUtils.ts
// Booking-specific utility functions

import { Booking, BookingListItem } from '../../../services/api';
import {
  formatDateForDisplay,
  calculateDaysBetween,
  formatCurrency,
} from '../base/listUtils';

/**
 * Format booking dates as JSX with icon for display
 */
export const formatBookingDateRange = (
  booking: Booking | BookingListItem
): string => {
  const start = formatDateForDisplay(booking.checkInDate);
  const end = formatDateForDisplay(booking.checkOutDate);
  return `${start} > ${end}`;
};

/**
 * Format booking dates as string for aria-label and other text-only uses
 */
export const formatBookingDateRangeString = (
  booking: Booking | BookingListItem
): string => {
  const start = formatDateForDisplay(booking.checkInDate);
  const end = formatDateForDisplay(booking.checkOutDate);
  return `${start} → ${end}`;
};

/**
 * Result of formatQuoteExpiry: the label string to show, plus an isExpired
 * flag the caller uses to switch colour/styling on the row.
 */
export interface QuoteExpiryResult {
  label: string;
  isExpired: boolean;
}

/**
 * Format a quote's expiry date for the BookingsList Expires row.
 *
 * Examples:
 *   "Expires: 11 Jun 2026 (in 2 days)"   - future
 *   "Expires: 11 Jun 2026 (tomorrow)"    - exactly 1 day away
 *   "Expires: 11 Jun 2026 (today)"       - today
 *   "Expired: 4 Jun 2026 (3 days ago)"   - past
 *   "Expired: 4 Jun 2026 (yesterday)"    - exactly 1 day ago
 *
 * Day deltas are computed from calendar-day boundaries (start of day),
 * not from now-to-now, so "tomorrow" and "yesterday" feel natural even
 * when the time-of-day is close to midnight.
 */
export const formatQuoteExpiry = (
  quoteExpiresAt: string
): QuoteExpiryResult => {
  const expiry = new Date(quoteExpiresAt);
  const dateLabel = formatDateForDisplay(quoteExpiresAt);

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const startOfExpiry = new Date(
    expiry.getFullYear(),
    expiry.getMonth(),
    expiry.getDate()
  );

  const msPerDay = 1000 * 60 * 60 * 24;
  const dayDelta = Math.round(
    (startOfExpiry.getTime() - startOfToday.getTime()) / msPerDay
  );

  const isExpired = dayDelta < 0;

  let relative: string;
  if (dayDelta > 1) relative = `in ${dayDelta} days`;
  else if (dayDelta === 1) relative = 'tomorrow';
  else if (dayDelta === 0) relative = 'today';
  else if (dayDelta === -1) relative = 'yesterday';
  else relative = `${Math.abs(dayDelta)} days ago`;

  const prefix = isExpired ? 'Expired' : 'Expires';
  return {
    label: `${prefix}: ${dateLabel} (${relative})`,
    isExpired,
  };
};

/**
 * Format pet type counts for booking display
 * e.g., "(2 Dogs, 1 Cat, 1 Bird)"
 */
export const formatBookingPetTypeCounts = (
  booking: BookingListItem
): string => {
  if (!booking.pets || booking.pets.length === 0) {
    return '(No pets)';
  }

  // Count pets by type
  const typeCounts = booking.pets.reduce(
    (acc, pet) => {
      const type = pet.petType || 'Unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  // Format as "2 Dogs, 1 Cat, 1 Bird"
  const formatted = Object.entries(typeCounts)
    .map(([type, count]) => {
      const pluralized =
        count === 1 ? type : `${type}${type.endsWith('s') ? 'es' : 's'}`;
      return `${count} ${pluralized}`;
    })
    .join(', ');

  return `(${formatted})`;
};

/**
 * Get status badge color based on booking status
 */
export interface StatusColorResult {
  bg: string;
  text: string;
}

export const getStatusColor = (status: string): StatusColorResult => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return { bg: '#FFF3E0', text: '#E65100' }; // Orange
    case 'confirmed':
      return { bg: '#E8F5E9', text: '#2E7D32' }; // Green
    case 'checked in':
      return { bg: '#E3F2FD', text: '#1565C0' }; // Blue
    case 'checked out':
      return { bg: '#F3E5F5', text: '#6A1B9A' }; // Purple
    case 'cancelled':
      return { bg: '#FFEBEE', text: '#C62828' }; // Red
    default:
      return { bg: '#F5F5F5', text: '#424242' }; // Grey
  }
};

/**
 * Format booking summary for display
 * e.g., "Fluffy (Dog) - 5 nights - R 500.00"
 */
export const formatBookingSummary = (booking: Booking): string => {
  const petName = booking.pet?.name || 'Unknown Pet';
  const petType = booking.pet?.petType?.type || 'Pet';
  const days = calculateDaysBetween(booking.checkInDate, booking.checkOutDate);
  const amount = formatCurrency(booking.totalAmount);

  return `${petName} (${petType}) - ${days} night${days !== 1 ? 's' : ''} - ${amount}`;
};

/**
 * Format client and pet info for booking display
 */
export const formatClientAndPet = (booking: Booking): string => {
  const clientName = booking.client
    ? `${booking.client.firstName} ${booking.client.lastName}`
    : 'Unknown Client';
  const petName = booking.pet?.name || 'Unknown Pet';
  return `${clientName} - ${petName}`;
};

/**
 * Get status icon based on booking status
 */
export const getStatusIcon = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return '⏳'; // Hourglass
    case 'confirmed':
      return '✓'; // Check
    case 'checked in':
      return '→'; // Arrow
    case 'checked out':
      return '✓✓'; // Double check
    case 'cancelled':
      return '✗'; // X
    default:
      return '?';
  }
};
